import React, { useState, useEffect } from 'react';
import { updateUserAPI, sendPasswordResetAPI } from '../services/serverService';
import { useAuth } from '../context/AuthContext';

const Spinner = () => <div className="spinner"></div>;

const EditUserModal = ({ user, closeModal, refreshList }) => {

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phoneCode: '+44',
        phoneNumber: '',
        password: '', 
        permissionLevel: '',
        isSupportManager: false
    });

    useEffect(() => {
        if (user) {
            setFormData({
                fullName: user.fullName || '',
                email: user.email || '',
                phoneCode: user.phoneCode || '+44',
                phoneNumber: user.phoneNumber || '',
                password: '', 
                permissionLevel: user.permissionLevel || user.role || '',
                isSupportManager: user.isSupportManager || false
            });
        }
    }, [user]);

    const [isSaving, setIsSaving] = useState(false);
    const [apiError, setApiError] = useState('');
    const [resetEmailSent, setResetEmailSent] = useState(false); 

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleCheckboxChange = (e) => {
        setFormData(prevState => ({ ...prevState, isSupportManager: e.target.checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setApiError('');
        try {
            const targetId = user.id || user.uid || user.email;
            // formData now automatically includes isSupportManager!
            await updateUserAPI(targetId, formData);
            
            alert('User updated successfully!');
            refreshList(); 
            closeModal();  
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Update failed.';
            setApiError(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCopyUID = () => {
        // user.id in list view maps to the local document ID
        navigator.clipboard.writeText(user.uid || user.id);
        alert("Global User ID copied to clipboard!");
    };

    const handleSendResetEmail = async () => {
        const confirmAlert = window.confirm(`Send a password reset email to ${formData.email}?`);
        if (!confirmAlert) return;

        try {
            const targetId = user.id || user.uid || user.email;

            await sendPasswordResetAPI({
                userId: targetId,
                email: formData.email,
                username: user.username,
                fullName: formData.fullName
            });
            
            setResetEmailSent(true);
            alert(`Password reset email sent safely to ${formData.email}!`);
        } catch (error) {
            console.error("Reset Password Error:", error);
            alert("Failed to send reset email. Please check the logs.");
        }
    };

    const { userRole, isSupportManager: currentUserIsManager } = useAuth();

    // 1. The person editing MUST be a Dev or a Support Manager
    const canGrantManager = userRole === 'Dev' || currentUserIsManager;

    // 2. The person being edited MUST be eligible to hold the privilege
    const isEligibleTarget = formData.permissionLevel === 'Support' || formData.permissionLevel === 'Dev' || user?.role === 'Global Admin';

    // 3. Only show if BOTH are true
    const showManagerCheckbox = canGrantManager && isEligibleTarget;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <button className="modal-close-button" onClick={closeModal}>&times;</button>
                
                <h2>Edit User: <span style={{ color: 'var(--primary-blue)' }}>{user.username}</span></h2>
                
                <div style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#7f8c8d' }}>
                    <strong>Global User ID: </strong> 
                    <span style={{ fontFamily: 'monospace', backgroundColor: '#eee', padding: '2px 5px', borderRadius: '4px' }}>
                        {user.uid || user.id}
                    </span>
                    <button 
                        type="button"
                        onClick={handleCopyUID}
                        style={{ marginLeft: '10px', padding: '2px 8px', fontSize: '0.8rem', cursor: 'pointer' }}
                        className="button-secondary"
                    >
                        Copy
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Full Name</label>
                            <input name="fullName" value={formData.fullName} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label>Email Address</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                        </div>
                        
                        <div className="form-group">
                            <label>Phone Number</label>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <input 
                                    type="text" 
                                    name="phoneCode" 
                                    value={formData.phoneCode} 
                                    onChange={handleChange} 
                                    placeholder="+44" 
                                    style={{ width: '60px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} 
                                />
                                <input 
                                    type="tel" 
                                    name="phoneNumber" 
                                    value={formData.phoneNumber} 
                                    onChange={handleChange} 
                                    placeholder="7575944496"
                                    style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} 
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>New Password</label>
                            <input type="password" name="password" placeholder="Leave blank to keep unchanged" onChange={handleChange} />
                        </div>

                        {showManagerCheckbox && (
                            <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '10px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0, cursor: 'pointer', fontWeight: 'normal' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={formData.isSupportManager}
                                        onChange={handleCheckboxChange}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <strong>Grant Support Manager Privileges</strong> 
                                    <span style={{ fontSize: '12px', color: '#718096' }}>(Access to Routing Rules & Billing Tickets)</span>
                                </label>
                            </div>
                        )}
                    </div>

                    <div style={{ marginTop: '1rem', padding: '10px', backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '6px' }}>
                        <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}><strong>Security Options:</strong></p>
                        <button 
                            type="button" 
                            className="button-secondary"
                            onClick={handleSendResetEmail}
                            disabled={resetEmailSent}
                        >
                            {resetEmailSent ? "Reset Email Sent ✓" : "Send Password Reset Email"}
                        </button>
                    </div>

                    {apiError && <p className="error-message">{apiError}</p>}
                    
                    <div className="modal-actions">
                        <button type="button" className="button-secondary" onClick={closeModal} disabled={isSaving}>Close</button>
                        <button type="submit" className="button-primary" disabled={isSaving}>{isSaving ? <Spinner /> : 'Save Changes'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditUserModal;